import { FC } from 'react';
import {
  Pencil,
  Copy,
  Trash2,
  Send,
  Clock,
  Layers,
  Box,
} from 'lucide-react';
import { IFieldWorkout, TacticalPhase } from '../../../models/field-programming';
import styles from './FieldWorkoutsTab.module.scss';

interface Props {
  workout: IFieldWorkout;
  onOpen: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onReassign: () => void;
  onDelete: () => void;
}

// NOTE: 4 dei valori enum (activation/athletic/technical/compensative) non hanno
// ancora label/colore definiti nel dominio — accesso reso difensivo via Partial.
const PHASE_COLORS: Partial<Record<TacticalPhase, string>> = {
  warmup: '#f59e0b',
  situational: '#6366f1',
  match: '#10b981',
  cool_down: '#0ea5e9',
};

const PHASE_LABELS: Partial<Record<TacticalPhase, string>> = {
  warmup: 'Riscaldamento',
  situational: 'Situazionale',
  match: 'Partita',
  cool_down: 'Defaticamento',
};

const FieldWorkoutCard: FC<Props> = ({
  workout,
  onOpen,
  onEdit,
  onDuplicate,
  onReassign,
  onDelete,
}) => {
  const totalExercises = workout.blocks.reduce(
    (sum, b) => sum + (b.exercises?.length ?? 0),
    0,
  );

  return (
    <div className={styles.card}>
      <div className={styles.cardBody}>
        {/* Header */}
        <div className={styles.cardHeader}>
          <div className={styles.cardTitleArea} onClick={onOpen} style={{ cursor: 'pointer' }}>
            <p className={styles.workoutTitle}>{workout.name}</p>
            <div className={styles.cardBadges}>
              {workout.sport && (
                <span className={styles.typeBadge}>{workout.sport}</span>
              )}
              {workout.isTemplate && (
                <span className={styles.leopartBadge}>Template</span>
              )}
            </div>
          </div>
          <div className={styles.cardActions}>
            <button
              className={styles.actionIconBtn}
              onClick={onEdit}
              title="Modifica"
            >
              <Pencil size={14} />
            </button>
            <button
              className={styles.actionIconBtn}
              onClick={onDuplicate}
              title="Duplica"
            >
              <Copy size={14} />
            </button>
            <button
              className={`${styles.actionIconBtn} ${styles.actionIconBtnDanger}`}
              onClick={onDelete}
              title="Elimina"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>

        {/* Block preview */}
        {workout.blocks.length > 0 && (
          <div className={styles.dayPreview} onClick={onOpen} style={{ cursor: 'pointer' }}>
            {workout.blocks.slice(0, 3).map((block, i) => (
              <div key={i} className={styles.dayItem}>
                <span className={styles.dayLabel}>B{i + 1}</span>
                <span className={styles.dayTitle}>{block.snapshotName}</span>
                <span className={styles.dayBlockCount}>
                  {block.exercises?.length ?? 0}
                </span>
                <span
                  className={styles.blockDot}
                  style={{ background: PHASE_COLORS[block.tacticalPhase] }}
                  title={PHASE_LABELS[block.tacticalPhase]}
                />
              </div>
            ))}
            {workout.blocks.length > 3 && (
              <span className={styles.moreDays}>
                +{workout.blocks.length - 3} altr
                {workout.blocks.length - 3 === 1 ? 'o' : 'i'}
              </span>
            )}
          </div>
        )}

        {/* Description */}
        {workout.description && (
          <p className={styles.description} onClick={onOpen} style={{ cursor: 'pointer' }}>
            {workout.description}
          </p>
        )}

        {/* Tags */}
        {workout.tags.length > 0 && (
          <div className={styles.cardTags}>
            {workout.tags.slice(0, 3).map((tag) => (
              <span key={tag} className={styles.tag}>
                {tag}
              </span>
            ))}
            {workout.tags.length > 3 && (
              <span className={styles.tag}>+{workout.tags.length - 3}</span>
            )}
          </div>
        )}

        {/* Footer */}
        <div className={styles.cardFooter}>
          <div className={styles.cardMeta}>
            <span className={styles.metaItem}>
              <Layers size={13} strokeWidth={2.5} />
              {workout.blocks.length} blocc{workout.blocks.length === 1 ? 'o' : 'hi'}
            </span>
            {totalExercises > 0 && (
              <span className={styles.metaItem}>
                <Box size={13} strokeWidth={2.5} />
                {totalExercises} eserciz{totalExercises === 1 ? 'io' : 'i'}
              </span>
            )}
            {workout.defaultDurationMinutes && (
              <span className={styles.metaItem}>
                <Clock size={13} strokeWidth={2.5} />~
                {workout.defaultDurationMinutes} min
              </span>
            )}
          </div>
          <button className={styles.assignBtn} onClick={onReassign}>
            <Send size={12} strokeWidth={2.5} />
            Assegna
          </button>
        </div>
      </div>
    </div>
  );
};

export default FieldWorkoutCard;
